import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { dmClient } from '../../services/dmClient';
import LcdNcdBuildSettings, {
  DEFAULT_BUILD_SETTINGS,
  buildSettingsPayload,
  summarizeBuildSettings,
} from './LcdNcdBuildSettings';
import './admin.css';

const EMPTY_FILTERS = {
  policyId: '',
  title: '',
  cpt: '',
  icd: '',
  keywords: '',
  mac: '',
  state: '',
  dos: '',
  status: '',
  graphStatus: '',
};

const INCLUDE_MODES = [
  { id: 'selected', label: 'Selected policies only' },
  { id: 'articles', label: 'Directly related articles' },
  { id: 'related', label: 'Directly related LCDs and NCDs' },
];

function keyOf(item) {
  return `${item.policyType}|${item.documentId}|${item.version}`;
}

function errorText(err, fallback) {
  return err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function LcdNcdPage() {
  const [tab, setTab] = useState('search');
  const [policyType, setPolicyType] = useState('lcd');
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState('id');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forbidden, setForbidden] = useState(false);
  const [queue, setQueue] = useState([]);
  const [includeMode, setIncludeMode] = useState('selected');
  const [preview, setPreview] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [buildNote, setBuildNote] = useState('');
  const [history, setHistory] = useState(null);
  const [buildSettings, setBuildSettings] = useState(DEFAULT_BUILD_SETTINGS);
  const [buildOptions, setBuildOptions] = useState(null);

  const batchLimit = result?.batchLimit || 10;
  const configReady = result?.graphConfigReady !== false;
  const articleSearch = Boolean(buildOptions?.articleSearch ?? result?.articleSearch);
  const isArticle = policyType === 'article';
  const isNcd = policyType === 'ncd';
  const articleOff = isArticle && !articleSearch;

  const load = useCallback(async () => {
    if (policyType === 'article' && !articleSearch) {
      setResult(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await dmClient.get('/admin/cms-policies', {
        params: { type: policyType, sort, page, pageSize: 25, ...applied },
      });
      setForbidden(false);
      setResult(response.data);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      setError(errorText(err, 'Search failed'));
    } finally {
      setLoading(false);
    }
  }, [policyType, sort, page, applied, articleSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    dmClient.get('/admin/cms-policies/build-options')
      .then((response) => { if (!cancelled) setBuildOptions(response.data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) setForbidden(true);
      });
    return () => { cancelled = true; };
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const response = await dmClient.get('/admin/cms-policies/builds', { params: { page: 1, pageSize: 25 } });
      setHistory(response.data);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      setError(errorText(err, 'Could not load build history'));
    }
  }, []);

  useEffect(() => {
    if (tab !== 'history') return undefined;
    let cancelled = false;
    const tick = () => {
      dmClient.get('/admin/cms-policies/builds', { params: { page: 1, pageSize: 25 } })
        .then((response) => { if (!cancelled) setHistory(response.data); })
        .catch((err) => {
          if (cancelled) return;
          if (err?.response?.status === 403) setForbidden(true);
          setError(errorText(err, 'Could not load build history'));
        });
    };
    tick();
    const timer = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [tab]);

  const items = result?.items || [];
  const queuedKeys = useMemo(() => new Set(queue.map(keyOf)), [queue]);
  const pageSelected = items.length > 0 && items.every((item) => queuedKeys.has(keyOf(item)));

  const queueStats = useMemo(() => {
    const byType = {};
    let built = 0;
    let updates = 0;
    let warnings = 0;
    let bytes = 0;
    queue.forEach((item) => {
      byType[item.policyType] = (byType[item.policyType] || 0) + 1;
      if (item.graphStatus === 'Built') built += 1;
      if (item.graphStatus === 'Update Available') updates += 1;
      if ((item.warnings || []).length) warnings += 1;
      bytes += Number(item.sourceBytes) || 0;
    });
    return { byType, built, updates, warnings, bytes };
  }, [queue]);

  function setField(name, value) {
    setDraft((prev) => ({ ...prev, [name]: value }));
  }

  function mergeQueue(incoming) {
    setQueue((prev) => {
      const seen = new Set(prev.map(keyOf));
      const next = [...prev];
      incoming.forEach((item) => {
        const key = keyOf(item);
        if (!seen.has(key)) {
          seen.add(key);
          next.push(item);
        }
      });
      return next.slice(0, batchLimit);
    });
  }

  async function enrich(docs) {
    if (!docs.length) return [];
    const response = await dmClient.post('/admin/cms-policies/expand', {
      documents: docs.map((item) => ({
        ...item,
        rebuild: item.graphStatus === 'Built' || item.graphStatus === 'Update Available',
      })),
      includeMode: 'selected',
    });
    const described = response.data?.documents || [];
    return docs.map((item) => {
      const match = described.find((row) => keyOf(row) === keyOf(item));
      return match ? { ...item, ...match } : item;
    });
  }

  async function addItems(docs, { confirmed = false } = {}) {
    const fresh = docs.filter((item) => !queuedKeys.has(keyOf(item)));
    if (!fresh.length) return;
    const room = batchLimit - queue.length;
    if (fresh.length > room && !confirmed) {
      setConfirm({
        kind: 'select',
        total: fresh.length,
        items: fresh.slice(0, Math.max(room, 0)),
        room,
      });
      return;
    }
    const accepted = fresh.slice(0, Math.max(room, 0));
    if (!accepted.length) {
      setError(`The build queue already has ${batchLimit} policies.`);
      return;
    }
    try {
      mergeQueue(await enrich(accepted));
    } catch (err) {
      mergeQueue(accepted);
      setError(errorText(err, 'Policies were queued without a source check'));
    }
  }

  function toggleRow(item) {
    if (queuedKeys.has(keyOf(item))) {
      setQueue((prev) => prev.filter((row) => keyOf(row) !== keyOf(item)));
      return;
    }
    addItems([item]);
  }

  function togglePage() {
    if (pageSelected) {
      const pageKeys = new Set(items.map(keyOf));
      setQueue((prev) => prev.filter((row) => !pageKeys.has(keyOf(row))));
      return;
    }
    addItems(items);
  }

  async function selectAllMatches() {
    try {
      const response = await dmClient.get('/admin/cms-policies', {
        params: { type: policyType, sort, idsOnly: 1, ...applied },
      });
      const matched = response.data?.items || [];
      const total = response.data?.total || matched.length;
      if (total > matched.length) {
        setConfirm({ kind: 'select-all', total, items: matched });
        return;
      }
      addItems(matched);
    } catch (err) {
      setError(errorText(err, 'Could not select matches'));
    }
  }

  async function openPreview(item) {
    setShowRaw(false);
    setPreview({ loading: true, policyType: item.policyType, documentId: item.documentId });
    try {
      const response = await dmClient.get(
        `/admin/cms-policies/${item.policyType}/${item.documentId}`,
        { params: { version: item.version } },
      );
      setPreview(response.data?.policy || null);
    } catch (err) {
      setPreview(null);
      setError(errorText(err, 'Could not open this policy'));
    }
  }

  function documentsForSubmit() {
    return queue.map((item) => ({
      policyType: item.policyType,
      documentId: item.documentId,
      version: item.version,
      title: item.title,
      rebuild: item.graphStatus === 'Built' || item.graphStatus === 'Update Available',
    }));
  }

  async function requestBuild() {
    setBusy(true);
    setBuildNote('');
    setError('');
    try {
      const response = await dmClient.post('/admin/cms-policies/expand', {
        documents: documentsForSubmit(),
        includeMode,
      });
      setConfirm({ kind: 'build', plan: response.data });
    } catch (err) {
      setError(errorText(err, 'Could not prepare this batch'));
    } finally {
      setBusy(false);
    }
  }

  async function confirmBuild() {
    setBusy(true);
    setError('');
    try {
      const response = await dmClient.post('/admin/cms-policies/builds', {
        documents: documentsForSubmit(),
        includeMode,
        acknowledgeLimit: true,
        buildSettings: buildSettingsPayload(buildSettings),
      });
      const results = response.data?.results || [];
      const queued = new Set(results.filter((item) => item.outcome === 'queued').map(keyOf));
      setQueue((prev) => prev.filter((item) => !queued.has(keyOf(item))));
      const failed = results.filter((item) => item.outcome !== 'queued');
      setBuildNote(
        failed.length
          ? `Queued ${queued.size}. ${failed.length} were not submitted.`
          : `Queued ${queued.size} ${queued.size === 1 ? 'policy' : 'policies'}.`,
      );
      setConfirm(null);
      setTab('history');
      load();
      loadHistory();
    } catch (err) {
      setError(errorText(err, 'Build submission failed'));
    } finally {
      setBusy(false);
    }
  }

  async function retryBuild(buildId) {
    setError('');
    try {
      await dmClient.post('/admin/cms-policies/builds/retry', {
        buildIds: [buildId],
        buildSettings: buildSettingsPayload(buildSettings),
      });
      loadHistory();
    } catch (err) {
      setError(errorText(err, 'Retry failed'));
    }
  }

  if (forbidden) {
    return (
      <div className="admin-page">
        <div className="admin-head"><h1>LCD/NCD graphs</h1></div>
        <div className="admin-alert admin-alert--error">Not authorized to manage LCD/NCD graphs.</div>
      </div>
    );
  }

  const plan = confirm?.plan;
  const willSubmit = (plan?.documents || []).filter((item) => item.included && item.buildable);
  const heldBack = (plan?.documents || []).filter((item) => item.included && !item.buildable);
  const buildSummary = summarizeBuildSettings(buildSettings, buildOptions);

  return (
    <div className="admin-page">
      <div className="admin-head">
        <h1>LCD/NCD graphs</h1>
        <p>Search Medicare policies and send a small batch through the existing graph builder.</p>
      </div>

      <div className="lcdncd-tabs">
        <button type="button" className={tab === 'search' ? 'is-active' : ''} onClick={() => setTab('search')}>Search</button>
        <button type="button" className={tab === 'history' ? 'is-active' : ''} onClick={() => setTab('history')}>Build history</button>
      </div>

      {error && <div className="admin-alert admin-alert--error">{error}</div>}
      {buildNote && <div className="admin-alert admin-alert--ok">{buildNote}</div>}
      {result && !result.graphConfigReady && result.graphConfigMessage && (
        <div className="admin-alert admin-alert--error">{result.graphConfigMessage}</div>
      )}

      <LcdNcdBuildSettings value={buildSettings} onChange={setBuildSettings} options={buildOptions} />

      {tab === 'search' && (
        <>
          <div className="admin-card">
            <div className="lcdncd-types">
              {['lcd', 'ncd', 'article'].map((type) => (
                <label key={type} className={type === 'article' && !articleSearch ? 'is-disabled' : ''}>
                  <input
                    type="radio"
                    name="policyType"
                    value={type}
                    checked={policyType === type}
                    disabled={type === 'article' && !articleSearch}
                    onChange={() => { setPolicyType(type); setPage(1); }}
                  />
                  {type === 'lcd' ? 'LCD' : type === 'ncd' ? 'NCD' : 'Article'}
                </label>
              ))}
              {!articleSearch && <span className="admin-hint">Article search stays off until that dataset is loaded.</span>}
            </div>

            {articleOff ? (
              <p className="admin-hint">Article tables are not loaded, so this search is disabled.</p>
            ) : (
              <form
                className="lcdncd-filters"
                onSubmit={(event) => {
                  event.preventDefault();
                  setPage(1);
                  setApplied({ ...draft });
                }}
              >
                <label>Policy id<input value={draft.policyId} onChange={(e) => setField('policyId', e.target.value)} placeholder={isNcd ? 'NCD id or section' : isArticle ? 'Article id' : 'LCD id'} /></label>
                <label>Title<input value={draft.title} onChange={(e) => setField('title', e.target.value)} /></label>
                <label>CPT / HCPCS
                  <input value={draft.cpt} disabled={isNcd} onChange={(e) => setField('cpt', e.target.value)} placeholder={isNcd ? 'No code crosswalk in this NCD extract' : 'Code'} />
                </label>
                <label>ICD-10
                  <input
                    value={draft.icd}
                    disabled={!articleSearch || isNcd}
                    onChange={(e) => setField('icd', e.target.value)}
                    placeholder={!articleSearch ? 'Waits until Article tables are loaded' : isNcd ? 'No code crosswalk in this NCD extract' : 'Code'}
                  />
                </label>
                <label>Clinical keywords<input value={draft.keywords} onChange={(e) => setField('keywords', e.target.value)} /></label>
                <label>MAC<input value={draft.mac} disabled={isNcd || isArticle} onChange={(e) => setField('mac', e.target.value)} placeholder={isNcd || isArticle ? 'LCDs only' : 'Contractor'} /></label>
                <label>State<input value={draft.state} disabled={isNcd || isArticle} onChange={(e) => setField('state', e.target.value)} placeholder={isNcd || isArticle ? 'LCDs only' : 'TX'} /></label>
                <label>Date of service<input type="date" value={draft.dos} onChange={(e) => setField('dos', e.target.value)} /></label>
                <label>Policy status
                  <select value={draft.status} onChange={(e) => setField('status', e.target.value)}>
                    <option value="">Any</option>
                    <option>In Effect</option>
                    <option>Future Effective</option>
                    <option>Retired</option>
                  </select>
                </label>
                <label>Graph status
                  <select value={draft.graphStatus} onChange={(e) => setField('graphStatus', e.target.value)}>
                    <option value="">Any</option>
                    <option>Not Built</option>
                    <option>Queued</option>
                    <option>Building</option>
                    <option>Built</option>
                    <option>Failed</option>
                    <option>Update Available</option>
                  </select>
                </label>
                <div className="lcdncd-actions">
                  <button className="admin-btn admin-btn--primary" type="submit">Search</button>
                  <button className="admin-btn" type="button" onClick={() => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(1); }}>Clear</button>
                </div>
              </form>
            )}
            {isNcd && <p className="admin-hint">This NCD extract has no CPT/HCPCS or ICD-10 crosswalk. Search by id, manual section, title, keywords, and dates.</p>}
          </div>

          {!articleOff && (
            <div className="admin-card lcdncd-results">
              <div className="lcdncd-toolbar">
                <span>{loading ? 'Searching…' : `${result?.total || 0} policies`}</span>
                {result?.summary && (
                  <span className="admin-hint">
                    Not built {result.summary.notBuilt} · Built {result.summary.built} · Update {result.summary.updateAvailable} · Failed {result.summary.failed}
                  </span>
                )}
                <label>Sort
                  <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}>
                    <option value="id">Document id</option>
                    <option value="relevance">Relevance</option>
                    <option value="title">Title</option>
                    <option value="updated">Latest update</option>
                    <option value="graph">Graph status</option>
                  </select>
                </label>
                <button type="button" className="admin-btn" onClick={selectAllMatches} disabled={!items.length}>Select all matches</button>
              </div>
              {(result?.notices || []).map((notice) => <p key={notice} className="admin-hint">{notice}</p>)}
              <div className="lcdncd-scroll">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked={pageSelected} onChange={togglePage} aria-label="Select this page" /></th>
                      <th>Id</th><th>Title</th><th>Type</th><th>Version</th><th>Status</th>
                      <th>Effective</th><th>MAC / states</th><th>Related</th><th>Graph</th><th>Last build</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={11} className="admin-empty">Loading…</td></tr>}
                    {!loading && items.length === 0 && <tr><td colSpan={11} className="admin-empty">No policies match.</td></tr>}
                    {!loading && items.map((item) => (
                      <tr key={keyOf(item)}>
                        <td>
                          <input
                            type="checkbox"
                            checked={queuedKeys.has(keyOf(item))}
                            onChange={() => toggleRow(item)}
                            aria-label={`Select ${item.publicId}`}
                          />
                        </td>
                        <td><button type="button" className="lcdncd-link" onClick={() => openPreview(item)}>{item.publicId}</button></td>
                        <td className="lcdncd-title">{item.title}</td>
                        <td>{item.policyType.toUpperCase()}</td>
                        <td>{item.version}</td>
                        <td>{item.policyStatus}</td>
                        <td>{item.effectiveDate ? String(item.effectiveDate).slice(0, 10) : '—'}</td>
                        <td>{[item.macs, item.states].filter(Boolean).join(' · ') || '—'}</td>
                        <td>{item.relatedCount}</td>
                        <td><span className={`lcdncd-badge lcdncd-badge--${item.graphStatus.replace(/\s+/g, '-').toLowerCase()}`}>{item.graphStatus}</span></td>
                        <td>{item.completedAt ? String(item.completedAt).slice(0, 10) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="lcdncd-pager">
                <button type="button" className="admin-btn" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>Previous</button>
                <span>Page {page}</span>
                <button type="button" className="admin-btn" disabled={page * (result?.pageSize || 25) >= (result?.total || 0)} onClick={() => setPage((n) => n + 1)}>Next</button>
              </div>
            </div>
          )}

          <div className="admin-card">
            <h3>Build queue</h3>
            <p className="admin-hint">
              {queue.length} selected
              {queueStats.byType.lcd ? ` · LCD ${queueStats.byType.lcd}` : ''}
              {queueStats.byType.ncd ? ` · NCD ${queueStats.byType.ncd}` : ''}
              {queueStats.byType.article ? ` · Article ${queueStats.byType.article}` : ''}
              {` · already built ${queueStats.built} · update available ${queueStats.updates} · with warnings ${queueStats.warnings} · ${formatBytes(queueStats.bytes)}`}
            </p>
            <label className="lcdncd-mode">Related documents
              <select value={includeMode} onChange={(e) => setIncludeMode(e.target.value)}>
                {INCLUDE_MODES.map((mode) => <option key={mode.id} value={mode.id}>{mode.label}</option>)}
              </select>
            </label>
            {includeMode === 'articles' && !articleSearch && <p className="admin-hint">Related article ids are listed and are not submitted. Their text is not loaded.</p>}
            <ul className="lcdncd-queue">
              {queue.length === 0 && <li className="admin-hint">Nothing selected.</li>}
              {queue.map((item) => (
                <li key={keyOf(item)}>
                  <span>{item.policyType.toUpperCase()} {item.publicId || item.documentId} v{item.version} — {item.title}</span>
                  {(item.warnings || []).map((warning) => <em key={warning}>{warning}</em>)}
                  <button type="button" className="admin-btn" onClick={() => setQueue((prev) => prev.filter((row) => keyOf(row) !== keyOf(item)))}>Remove</button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="admin-btn admin-btn--primary"
              disabled={!queue.length || !configReady || busy}
              onClick={requestBuild}
            >
              Build selected graphs
            </button>
          </div>
        </>
      )}

      {tab === 'history' && (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr><th>Submitted</th><th>Policy</th><th>Graph</th><th>Status</th><th>Error</th><th></th></tr>
            </thead>
            <tbody>
              {(history?.items || []).length === 0 && <tr><td colSpan={6} className="admin-empty">No builds yet.</td></tr>}
              {(history?.items || []).map((item) => (
                <tr key={item.buildId}>
                  <td>{item.submittedAt ? new Date(item.submittedAt).toLocaleString() : '—'}</td>
                  <td>{item.policyType?.toUpperCase()} {item.documentId} v{item.version}</td>
                  <td>{item.graphName || '—'}</td>
                  <td>{item.graphStatus}</td>
                  <td>{item.error || '—'}</td>
                  <td>{item.status === 'failed' && <button type="button" className="admin-btn" onClick={() => retryBuild(item.buildId)}>Retry</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <aside className="lcdncd-drawer" role="dialog" aria-label="Policy preview">
          <div className="lcdncd-drawer__bar">
            <strong>{preview.loading ? 'Loading…' : (preview.title || 'Policy')}</strong>
            <button type="button" className="admin-btn" onClick={() => setPreview(null)}>Close</button>
          </div>
          {!preview.loading && (
            <div className="lcdncd-drawer__body">
              <p>{preview.policyType?.toUpperCase()} {preview.publicId} · version {preview.version} · {preview.policyStatus}</p>
              <p className="admin-hint">
                Effective {preview.effectiveDate ? String(preview.effectiveDate).slice(0, 10) : '—'}
                {preview.macs ? ` · ${preview.macs}` : ''}
                {preview.states ? ` · ${preview.states}` : ''}
              </p>
              <p>Graph: {preview.graphStatus}{preview.graphName ? ` · ${preview.graphName}` : ''}</p>
              {(preview.warnings || []).map((warning) => <p key={warning} className="admin-hint">{warning}</p>)}
              <button type="button" className="admin-btn" onClick={() => setShowRaw((v) => !v)}>{showRaw ? 'View coverage' : 'View raw source'}</button>
              {showRaw ? (
                (preview.raw || []).map((section) => (
                  <section key={section.field}>
                    <h4>{section.field}</h4>
                    <pre>{section.text}</pre>
                  </section>
                ))
              ) : (
                (preview.sections || []).map((section) => (
                  <section key={section.field}>
                    <h4>{section.label} <span className="admin-hint">({section.field})</span></h4>
                    <div dangerouslySetInnerHTML={{ __html: section.html }} />
                  </section>
                ))
              )}
              <h4>Related</h4>
              <ul>
                {(preview.related || []).length === 0 && <li className="admin-hint">None recorded.</li>}
                {(preview.related || []).map((rel) => (
                  <li key={`${rel.kind}-${rel.documentId}-${rel.version}`}>
                    {rel.relationship}: {rel.policyType?.toUpperCase()} {rel.documentId} v{rel.version}
                    {rel.title ? ` — ${rel.title}` : ''}
                    {!rel.available && <em> · {rel.reason}</em>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      )}

      {confirm && (
        <div className="lcdncd-modal" role="dialog" aria-label="Confirm">
          <div className="admin-card">
            {confirm.kind === 'build' && plan && (
              <>
                <h3>Build these policies</h3>
                <p className="admin-hint">
                  {buildSummary.baseName} · {buildSummary.workspaceName} · {buildSummary.approach} · {buildSummary.model}
                  {buildSummary.autoGeneratePrompt ? '' : ' · prompt generation off'}
                  {buildSummary.description ? ` · ${buildSummary.description}` : ''}
                </p>
                {willSubmit.some((item) => ['Built', 'Update Available', 'Failed'].includes(item.graphStatus)) && (
                  <p>A new graph will be created. The existing graph stays.</p>
                )}
                {plan.exceedsLimit && <p>Only the first {plan.batchLimit} will be submitted. The rest stay out of this batch.</p>}
                <ul>
                  {willSubmit.map((item) => (
                    <li key={keyOf(item)}>
                      {item.policyType.toUpperCase()} {item.documentId} v{item.version} — {item.title}
                      {['Built', 'Update Available', 'Failed'].includes(item.graphStatus) ? ' · new graph' : ''}
                      {item.relationship ? ` · ${item.relationship}` : ''}
                      {(item.warnings || []).map((warning) => <em key={warning}> · {warning}</em>)}
                    </li>
                  ))}
                  {heldBack.map((item) => (
                    <li key={keyOf(item)}>
                      {item.policyType.toUpperCase()} {item.documentId} v{item.version} — {item.title}
                      {' · not submitted'}
                      {(item.warnings || []).map((warning) => <em key={warning}> · {warning}</em>)}
                    </li>
                  ))}
                </ul>
                {(plan.documents || []).filter((item) => !item.included).length > 0 && (
                  <p className="admin-hint">{(plan.documents || []).filter((item) => !item.included).length} more matched and will not be submitted.</p>
                )}
                {(plan.unavailable || []).length > 0 && (
                  <p className="admin-hint">{plan.unavailable.length} related document(s) are not in the local tables and will not be submitted.</p>
                )}
                <div className="lcdncd-actions">
                  <button type="button" className="admin-btn admin-btn--primary" disabled={busy || !willSubmit.length} onClick={confirmBuild}>Submit {willSubmit.length}</button>
                  <button type="button" className="admin-btn" onClick={() => setConfirm(null)}>Cancel</button>
                </div>
              </>
            )}
            {confirm.kind !== 'build' && (
              <>
                <h3>Selection is larger than the batch</h3>
                <p>
                  {confirm.total} policies match. The queue holds {batchLimit}. Only the first {confirm.items?.length || 0} of the ones that still fit will be added.
                </p>
                <div className="lcdncd-actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={() => { const next = confirm.items || []; setConfirm(null); addItems(next, { confirmed: true }); }}
                  >
                    Add {confirm.items?.length || 0}
                  </button>
                  <button type="button" className="admin-btn" onClick={() => setConfirm(null)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
